import React, { useState, useRef, useEffect, useMemo } from "react";
import "./FilterPills.css";

// Semantic category icons mapping
const CATEGORY_ICONS = {
  // Case Studies
  "All": "fa-solid fa-border-all",
  "All Case Studies": "fa-solid fa-border-all",
  "All Posts": "fa-solid fa-border-all",
  "All Industries": "fa-solid fa-border-all",
  "Healthcare & Mental Health": "fa-solid fa-heart-pulse",
  "Non-Profit & Community Fleet": "fa-solid fa-users",
  "Commercial Cleaning & Housekeeping": "fa-solid fa-broom",
  "Support Management & Integrations": "fa-solid fa-gear",
  "Recruitment & Digital Growth": "fa-solid fa-chart-column",
  "Beauty & Wellness Platform": "fa-solid fa-spa",

  // Media / Blog
  "Tech & Innovation": "fa-solid fa-microchip",
  "SaaS & Products": "fa-solid fa-cubes",
  "Talent & Culture": "fa-solid fa-people-roof",
  "Client Success": "fa-solid fa-trophy",
};

const getCategoryIcon = (category) => {
  return CATEGORY_ICONS[category] || "fa-solid fa-layer-group";
};

/**
 * Modern "All + Branded Category/Industry Selector" Component
 *
 * @param {Array<string>} categories - List of category names
 * @param {string} selectedCategory - Currently active category
 * @param {Function} onSelectCategory - Callback when a category is chosen
 * @param {Array<Object>} items - Optional list of item objects to calculate dynamic counts
 * @param {Object} categoryCounts - Optional precomputed map of { category: count }
 * @param {string} allLabel - Label for the primary "All" button (default: "All Case Studies")
 * @param {string} dropdownLabel - Label for the selector dropdown (default: "Browse by Industry")
 * @param {string} className - Optional extra CSS class
 */
const FilterPills = ({
  categories = [],
  selectedCategory,
  onSelectCategory,
  items = [],
  categoryCounts = null,
  allLabel = "All Case Studies",
  allDropdownLabel = null,
  dropdownLabel = "Browse by Industry",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);

  // Compute category counts dynamically if items are passed and counts not provided
  const counts = useMemo(() => {
    if (categoryCounts) return categoryCounts;

    const map = {};
    if (items && items.length > 0) {
      map["All"] = items.length;
      map["All Case Studies"] = items.length;
      map["All Posts"] = items.length;
      map["All Industries"] = items.length;

      items.forEach((item) => {
        const cat = item.category;
        if (cat) {
          map[cat] = (map[cat] || 0) + 1;
        }
      });
    }
    return map;
  }, [items, categoryCounts]);

  const totalCount = counts["All"] || counts[allLabel] || (items && items.length) || 0;

  // Normalize "All" checking
  const isAllSelected =
    selectedCategory === "All" ||
    selectedCategory === "All Posts" ||
    selectedCategory === "All Case Studies" ||
    selectedCategory === "All Industries";

  // List of specific categories (excluding "All")
  const specificCategories = useMemo(() => {
    return categories.filter(
      (c) =>
        c !== "All" &&
        c !== "All Posts" &&
        c !== "All Case Studies" &&
        c !== "All Industries"
    );
  }, [categories]);

  // Filtered categories in dropdown based on search
  const filteredDropdownCategories = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return specificCategories;
    return specificCategories.filter((c) => c.toLowerCase().includes(q));
  }, [specificCategories, searchQuery]);

  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = (category) => {
    onSelectCategory(category);
    setIsOpen(false);
    setSearchQuery("");
  };

  const resolvedAllDropdownLabel =
    allDropdownLabel ||
    (dropdownLabel.toLowerCase().includes("industr")
      ? "All Industries"
      : allLabel || "All");
  const resolvedSearchPlaceholder =
    dropdownLabel.toLowerCase().includes("categor")
      ? "Search categories..."
      : "Search industries...";
  const resolvedEmptyText =
    dropdownLabel.toLowerCase().includes("categor")
      ? "No matching categories found"
      : "No matching industries found";

  return (
    <div className={`filter-selector-wrapper ${className}`} ref={dropdownRef}>
      <div className="filter-controls-bar">
        {/* 1. All Button */}
        <button
          type="button"
          className={`filter-control-pill filter-pill-all ${
            isAllSelected ? "active" : ""
          }`}
          onClick={() => handleSelect(categories[0] || "All")}
          aria-pressed={isAllSelected}
        >
          <i className="fa-solid fa-list-ul filter-pill-icon"></i>
          <span className="filter-pill-label">{allLabel}</span>
          {totalCount > 0 && (
            <span className="filter-pill-count-badge">{totalCount}</span>
          )}
        </button>

        {/* 2. Browse by Industry / Category Selector Button */}
        <div className="filter-dropdown-anchor">
          <button
            type="button"
            className={`filter-control-pill filter-pill-dropdown ${
              !isAllSelected ? "category-active" : ""
            } ${isOpen ? "open" : ""}`}
            onClick={() => setIsOpen((prev) => !prev)}
            aria-expanded={isOpen}
            aria-haspopup="true"
          >
            <i
              className={`${
                !isAllSelected
                  ? getCategoryIcon(selectedCategory)
                  : dropdownLabel.toLowerCase().includes("categor")
                  ? "fa-solid fa-cubes"
                  : "fa-solid fa-building-user"
              } filter-pill-icon filter-green-icon`}
            ></i>
            <span className="filter-pill-label">
              {!isAllSelected ? selectedCategory : dropdownLabel}
            </span>
            <i
              className={`fa-solid fa-chevron-down filter-chevron-icon ${
                isOpen ? "rotate-up" : ""
              }`}
            ></i>
          </button>

          {/* 3. Dropdown Menu Card */}
          {isOpen && (
            <div className="filter-dropdown-menu" role="menu">
              {/* Search Box */}
              <div className="filter-dropdown-search-box">
                <i className="fa-solid fa-magnifying-glass filter-dropdown-search-icon"></i>
                <input
                  type="text"
                  className="filter-dropdown-search-input"
                  placeholder={resolvedSearchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="filter-dropdown-search-clear"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                )}
              </div>

              {/* Items List */}
              <div className="filter-dropdown-list">
                {/* All Option */}
                {!searchQuery && (
                  <button
                    type="button"
                    className={`filter-dropdown-item ${
                      isAllSelected ? "active" : ""
                    }`}
                    onClick={() => handleSelect(categories[0] || "All")}
                    role="menuitem"
                  >
                    <div className="filter-dropdown-item-left">
                      <i
                        className={`${getCategoryIcon(
                          "All"
                        )} filter-dropdown-item-icon`}
                      ></i>
                      <span className="filter-dropdown-item-name">
                        {resolvedAllDropdownLabel}
                      </span>
                    </div>
                    {totalCount > 0 && (
                      <span className="filter-dropdown-item-count">
                        {totalCount}
                      </span>
                    )}
                  </button>
                )}

                {/* Specific Category Options */}
                {filteredDropdownCategories.map((category) => {
                  const isSelected = selectedCategory === category;
                  const count = counts[category];
                  return (
                    <button
                      key={category}
                      type="button"
                      className={`filter-dropdown-item ${
                        isSelected ? "active" : ""
                      }`}
                      onClick={() => handleSelect(category)}
                      role="menuitem"
                    >
                      <div className="filter-dropdown-item-left">
                        <i
                          className={`${getCategoryIcon(
                            category
                          )} filter-dropdown-item-icon`}
                        ></i>
                        <span className="filter-dropdown-item-name">
                          {category}
                        </span>
                      </div>
                      {count !== undefined && (
                        <span className="filter-dropdown-item-count">
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}

                {filteredDropdownCategories.length === 0 && (
                  <div className="filter-dropdown-empty">
                    <i className="fa-regular fa-folder-open mb-2 d-block"></i>
                    {resolvedEmptyText}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterPills;
